import React from "react";
import { AlertCircle, Check, Loader2, X } from "lucide-react";

export const MAINTENANCE_LOG_FEATURE_LABEL = "Maintenance Log";

export function middleEllipsis(value, maxLength = 28) {
  const text = String(value ?? "");
  if (text.length <= maxLength) return text;
  const keep = Math.max(4, maxLength - 1);
  const front = Math.ceil(keep * 0.55);
  const back = keep - front;
  return `${text.slice(0, front)}…${text.slice(-back)}`;
}

export default function MaintenanceExtractStatusPill({
  featureLabel = MAINTENANCE_LOG_FEATURE_LABEL,
  status,
  jobCode = "",
  fileCount = 0,
  fileName = "",
  colors,
  onActivate,
  onDismiss,
}) {
  const isRunning = status === "running";
  const isSuccess = status === "success";
  const isError = status === "error";
  if (!isRunning && !isSuccess && !isError) return null;

  const fullFileName = String(fileName || "").trim();
  const truncatedName = middleEllipsis(fullFileName, 28);
  const useTwoLineRunning =
    isRunning && fileCount === 1 && fullFileName.length > 22;

  let primaryText = "";
  let secondaryText = "";
  let titleAttr = undefined;

  if (isRunning) {
    if (fileCount === 1 && fullFileName) {
      if (useTwoLineRunning) {
        primaryText = "Extracting maintenance log file…";
        secondaryText = truncatedName;
        titleAttr = fullFileName;
      } else {
        primaryText = `${featureLabel} · Extracting ${truncatedName}…`;
        titleAttr = fullFileName;
      }
    } else {
      primaryText = `${featureLabel} · Extracting ${fileCount} file${
        fileCount === 1 ? "" : "s"
      }…`;
    }
  } else if (isSuccess) {
    primaryText = `${featureLabel}${
      jobCode ? ` · ${jobCode}` : ""
    } · Extraction complete. Review now`;
  } else {
    primaryText = `${featureLabel} · Extraction failed. View`;
  }

  const interactive = isSuccess || isError;

  return (
    <div
      className="fixed bottom-4 left-1/2 z-[10040] flex w-[min(100%-2rem,420px)] -translate-x-1/2 justify-center"
      aria-live="polite"
    >
      <div
        role={interactive ? "button" : "status"}
        tabIndex={interactive ? 0 : undefined}
        onClick={interactive ? onActivate : undefined}
        onKeyDown={
          interactive
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onActivate?.();
                }
              }
            : undefined
        }
        title={titleAttr}
        className={`relative flex w-full max-w-[420px] items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#38bdf8] ${
          interactive ? "cursor-pointer" : "cursor-default"
        }`}
        style={{
          backgroundColor: colors?.inputBg || "#0f1f30",
          borderColor: colors?.border || "#1b3a4b",
          color: colors?.text || "#ffffff",
        }}
        aria-label={primaryText}
      >
        <span className="mt-0.5 shrink-0" aria-hidden="true">
          {isRunning ? (
            <Loader2 className="h-4 w-4 animate-spin" style={{ color: "#38bdf8" }} />
          ) : isSuccess ? (
            <Check className="h-4 w-4" style={{ color: "#22c55e" }} />
          ) : (
            <AlertCircle className="h-4 w-4" style={{ color: "#ef4444" }} />
          )}
        </span>
        <span className={`min-w-0 flex-1 ${interactive && onDismiss ? "pr-6" : ""}`}>
          {useTwoLineRunning ? (
            <>
              <span className="block font-semibold leading-snug">
                {primaryText}
              </span>
              <span
                className="mt-0.5 block truncate text-xs leading-snug"
                style={{ color: colors?.textSecondary || "#94a3b8" }}
                title={titleAttr}
              >
                {secondaryText}
              </span>
            </>
          ) : (
            <span className="block leading-snug">{primaryText}</span>
          )}
        </span>
        {!isRunning && onDismiss ? (
          <button
            type="button"
            aria-label="Dismiss"
            title="Dismiss"
            onClick={(event) => {
              event.stopPropagation();
              onDismiss();
            }}
            className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#38bdf8]"
            style={{ color: colors?.textSecondary || "#94a3b8" }}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
